import axios from "axios";
import Cookies from "universal-cookie";
import { API_BASE } from "../config/api";

// Marks a request as one the 401-interceptor below must never retry via a
// silent token refresh — a per-request opt-in flag rather than matching on
// `config.url` substrings (e.g. '/user/login'), which silently misses any
// sibling pre-session endpoint whose URL doesn't happen to contain that
// exact substring (e.g. '/user/pos-login', the alternative POS login alias
// — '/user/pos-login'.includes('/user/login') is false).
declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

export interface ApiHeaders {
  'Content-Type': string;
  'Authorization'?: string;
}

// Thin wrapper around axios.post that always sets skipAuthRedirect — the
// one thing every pre-session POS call (login, the refresh call below,
// and any future one like a POS-login alias or forgot-password endpoint)
// must remember to do. A bare axios.post(...) call still works and still
// requires manually setting the flag; this exists so a new pre-session
// endpoint has an obvious, discoverable, correctly-named function to
// reach for instead of copying the flag by hand and risking forgetting
// it.
export const preSessionPost = (url: string, data?: any, config?: import("axios").AxiosRequestConfig) =>
  axios.post(url, data, { ...config, skipAuthRedirect: true });

// The refresh token itself lives only in an httpOnly cookie set by the backend —
// this flag just makes sure the browser actually sends it on every request.
axios.defaults.withCredentials = true;

const cookies = new Cookies();

// The `auth` cookie only *caches* the access token for convenience — the real session
// boundary is the httpOnly refresh-token cookie (30 days, see app_be JWT_REFRESH_TTL).
// This must not expire sooner than a normal idle gap (a lunch break, an overnight tab),
// or the client loses its readable token before ever getting a chance to refresh it,
// silently rendering empty pages instead of the user just being asked to log back in.
export const AUTH_COOKIE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

let onAuthFailure: (() => void) | null = null;
export const registerAuthFailureHandler = (handler: () => void) => {
  onAuthFailure = handler;
};

export const getAuthHeaders = (auth: any): ApiHeaders => {
  const headers: ApiHeaders = {
    'Content-Type': 'application/json',
  };

  if (auth && auth.accessToken) {
    headers['Authorization'] = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

// Calls the refresh endpoint (refresh token comes from the httpOnly cookie automatically),
// persists the new access token to the auth cookie + axios default header for every
// other page/request to pick up, and returns it for the in-flight retry.
export const refreshAccessToken = async (): Promise<string> => {
  // preSessionPost (skipAuthRedirect): retrying this call's own 401 would
  // re-enter the in-flight single-flight refresh promise below and wait
  // on its own completion — a deadlock that leaves every page hanging
  // with no data instead of redirecting to the login screen.
  const res = await preSessionPost(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
  const accessToken = res.data.accessToken;

  const currentAuth = cookies.get("auth") || {};
  cookies.set("auth", { ...currentAuth, accessToken }, {
    path: '/',
    expires: new Date(Date.now() + AUTH_COOKIE_MAX_AGE_MS),
  });
  axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

  return accessToken;
};

let refreshPromise: Promise<string> | null = null;
const getOrRefreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

const forceReLogin = () => {
  cookies.remove("auth", { path: '/' });
  delete axios.defaults.headers.common['Authorization'];
  onAuthFailure?.();
};

// Every page reads the `auth` cookie once at render time and passes it in here, so by
// the time a long-idle tab wakes up that snapshot's accessToken may already be stale —
// always read a fresh cookie for the token instead of trusting the passed-in `auth`.
export const authenticatedFetch = async (url: string, options: RequestInit = {}, auth: any) => {
  let currentAuth = cookies.get("auth") || auth;

  // POS must NEVER fire an anonymous request. The catalog GETs (/store, /shop,
  // /brand, /category, /product) are intentionally public for mobile customers,
  // so a request without a Bearer token gets a 200 with EVERY store's data —
  // rendering other admins' stores in this admin's POS. If the access token is
  // missing (expired cookie, cleared cache), refresh it first; if the session
  // is truly dead, force a re-login instead of showing unscoped data.
  if (!currentAuth?.accessToken) {
    try {
      const accessToken = await getOrRefreshAccessToken();
      currentAuth = { ...currentAuth, accessToken };
    } catch (refreshError) {
      forceReLogin();
      return new Response(JSON.stringify({ success: false, message: "Session expired. Please log in again." }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  }

  const buildHeaders = (authData: any) => ({
    ...getAuthHeaders(authData),
    ...options.headers,
  });

  let res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: buildHeaders(currentAuth),
  });

  // Try a silent refresh on any 401 — even if the local `auth` cookie/access token is
  // already gone. The httpOnly refresh-token cookie is the real session and can still
  // be valid (it lives 30 days), so don't skip the retry just because the client-side
  // cache of the access token happened to expire or get cleared first.
  if (res.status === 401) {
    try {
      const accessToken = await getOrRefreshAccessToken();
      res = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: buildHeaders({ ...currentAuth, accessToken }),
      });
    } catch (refreshError) {
      forceReLogin();
    }
  }

  return res;
};

// One shared 401 handler for every axios call in the app (all pages call the bare
// `axios` module directly, so attaching this once here covers them all): try a
// single silent refresh, retry the original request, otherwise force a re-login.
export const installAuthInterceptor = (onFailure: () => void) => {
  registerAuthFailureHandler(onFailure);

  // Attach the token from the cookie at request time (not just once at app boot):
  // a hard page reload wipes axios' in-memory default header, and any bare-axios
  // call made before App's boot effect runs would otherwise go out anonymous —
  // which the public catalog endpoints answer with ALL stores' data.
  axios.interceptors.request.use((config) => {
    if (!config.headers?.Authorization) {
      const currentAuth = cookies.get("auth");
      if (currentAuth?.accessToken) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${currentAuth.accessToken}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      // skipAuthRedirect covers two cases, both opted in at their call site
      // rather than guessed from the URL (see the module augmentation
      // above for why): the refresh endpoint's own request (deadlock guard
      // — retrying it would re-enter the in-flight single-flight refresh
      // promise and wait on its own completion), and the login endpoint's
      // request (a wrong password is a normal, expected 401 with no prior
      // session to expire — without this it was treated the same as an
      // expired session, silently attempting a refresh guaranteed to fail
      // and falling through to forceReLogin()'s hard reload, wiping the
      // snackbar before the login page's own error message was ever seen).
      const skipAuthRedirect = originalRequest?.skipAuthRedirect === true;
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !skipAuthRedirect) {
        originalRequest._retry = true;
        try {
          const accessToken = await getOrRefreshAccessToken();
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          forceReLogin();
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
};
